import { type SpecialRateProgramUsage } from '@parkingbase/ecommerce-api-test';

import type {
  RateRow,
  TicketSearchResult,
} from '@/components/myaccount/parking-validation/change-discount/ChangeDiscount.types';

/**
 * Builds discount-program rate rows from a ticket-search result.
 *
 * ## Invariants:
 * - A program is `applies` only when a matching rateOutput exists with
 *   rateType 'SPECIALRATE' and rate >= 0. Why: the backend returns a rate for
 *   every program but only SPECIALRATE rows with a non-negative rate are valid
 *   to apply (this is exactly the PB-2292 eligibility check).
 * - actionState precedence: currently_applied → not_applicable → usage_exceeded → expired → selectable.
 */
export function buildRateRows(
  result: TicketSearchResult | null,
  appliedSpecialRateId: string | undefined,
  usage: SpecialRateProgramUsage[]
): RateRow[] {
  if (!result) return [];
  const programs = result.specialRatePrograms || [];
  const rateOutputs = result.rateOutputs || [];

  return programs.map((prog) => {
    const rateOutput = rateOutputs.find(
      (ro) => ro.rateCardId === prog.specialRateId && ro.rateType === 'SPECIALRATE'
    );
    const applies = Boolean(rateOutput && rateOutput.rate != null && rateOutput.rate >= 0);
    const rate = applies ? (rateOutput?.rate ?? null) : null;

    let actionState: RateRow['actionState'] = 'selectable';
    if (prog.specialRateId === appliedSpecialRateId) {
      actionState = 'currently_applied';
    } else if (!applies) {
      actionState = 'not_applicable';
    } else {
      const u = usage.find((x) => x.specialRateId === prog.specialRateId);
      if (u && u.open != null && u.usageCount != null && u.usageCount >= u.open) {
        actionState = 'usage_exceeded';
      }
      if (prog.endDateTime && new Date(prog.endDateTime) < new Date()) {
        actionState = 'expired';
      }
    }

    return {
      specialRateId: prog.specialRateId || '',
      specialRateName: prog.specialRateName || '',
      applies,
      rate,
      actionState,
      expiryDate: prog.endDateTime,
    };
  });
}
